/**
 * JourneyPage.jsx
 * صفحه سفر داستان‌محور پاترهد — از ورود تا هاگوارتز
 *
 * مراحل:
 *  welcome → platform_reveal → platform_confirm → running
 *    → [success] station_entry / ticket_needed / waiting_room / on_train / arrived
 *    → [failure] platform_failed
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import '../styles/journey.css';
import { useTranslation } from 'react-i18next';
import { sanitizePhoneInput, toEnglishDigits } from '../utils/phone';

const API = process.env.REACT_APP_API_URL || '/api';

const JALALI_MONTHS = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];
const CURRENT_JALALI_YEAR = Number(
  new Intl.DateTimeFormat('en-US-u-ca-persian', { year: 'numeric' })
    .format(new Date())
    .replace(/[^\d]/g, '')
);
const JALALI_YEARS = Array.from({ length: CURRENT_JALALI_YEAR - 1300 + 1 }, (_, i) => CURRENT_JALALI_YEAR - i);

function jalaliDaysInMonth(jm) {
  if (!jm) return 31;
  const m = Number(jm);
  if (m >= 1 && m <= 6) return 31;
  if (m >= 7 && m <= 11) return 30;
  return 30; // اسفند
}

// ── بوق قطار با Web Audio API ─────────────────────────────
function playTrainHorn() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const master = ctx.createGain();
    master.gain.setValueAtTime(0, ctx.currentTime);
    master.gain.linearRampToValueAtTime(0.38, ctx.currentTime + 0.06);
    master.gain.setValueAtTime(0.38, ctx.currentTime + 1.1);
    master.gain.linearRampToValueAtTime(0, ctx.currentTime + 1.6);
    master.connect(ctx.destination);

    // سه هارمونیک اصلی بوق قطار
    [[370, 0.5], [462, 0.35], [555, 0.22], [740, 0.12]].forEach(([freq, gain]) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(freq * 0.99, ctx.currentTime + 1.6);
      g.gain.value = gain;
      osc.connect(g);
      g.connect(master);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 1.7);
    });

    // فیلتر برای صدای بوق قطار بخار
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1200;
    master.connect(filter);
    filter.connect(ctx.destination);

    setTimeout(() => ctx.close(), 2000);
  } catch (_) {
    // Web Audio API در محیط‌هایی که پشتیبانی ندارند خطا نمی‌دهد
  }
}

// ── کمکی: ثانیه‌شمار ──────────────────────────────────────
function useCountdown(targetMs) {
  const [left, setLeft] = useState(Math.max(0, targetMs));
  useEffect(() => {
    if (!targetMs || targetMs <= 0) return;
    setLeft(targetMs);
    const id = setInterval(() => setLeft(p => Math.max(0, p - 1000)), 1000);
    return () => clearInterval(id);
  }, [targetMs]);
  const h = Math.floor(left / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const s = Math.floor((left % 60000) / 1000);
  return { left, h, m, s, formatted: `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}` };
}

// ── کمکی: ستاره‌ها ──────────────────────────────────────────
function Stars() {
  const stars = Array.from({ length: 80 }, (_, i) => ({
    id: i,
    top: Math.random() * 100,
    left: Math.random() * 100,
    size: Math.random() * 2.5 + 0.5,
    dur: Math.random() * 3 + 2,
    delay: Math.random() * 4,
  }));
  return (
    <div className="stars-bg" aria-hidden>
      {stars.map(s => (
        <div key={s.id} className="star" style={{
          top: `${s.top}%`, left: `${s.left}%`,
          width: s.size, height: s.size,
          '--dur': `${s.dur}s`, '--delay': `${s.delay}s`
        }} />
      ))}
    </div>
  );
}

// ── کمکی: لینک بازگشت به داشبورد ─────────────────────────────
// کاربر لاگین‌شده باید همیشه بتواند از هر نقطه‌ی مسیر سفر (هر مرحله‌ای که
// در آن باشد) به داشبورد خودش برگردد و هر وقت خواست ادامه‌ی مسیر را طی
// کند — داشبورد خودش راهنمای عبور از سکو را به‌صورت بنر نشان می‌دهد
// (UserDashboard.jsx).
function DashboardBackLink() {
  const { t } = useTranslation();
  return (
    <Link to="/dashboard" className="journey-dashboard-link" title={t('journey.dashboardLink')}>
      🏠 داشبورد من
    </Link>
  );
}

// ── کمکی: جغد ──────────────────────────────────────────────
function Owls() {
  const owls = Array.from({ length: 4 }, (_, i) => ({
    id: i,
    top: 10 + i * 20,
    dur: 10 + i * 3,
    delay: i * 4,
  }));
  return (
    <>
      {owls.map(o => (
        <div key={o.id} className="owl-particle"
          style={{ '--top': `${o.top}%`, '--dur': `${o.dur}s`, '--delay': `${o.delay}s` }}>
          🦉
        </div>
      ))}
    </>
  );
}

// ═══════════════════════════════════════════════
// صفحه اصلی
// ═══════════════════════════════════════════════
export default function JourneyPage() {
  const { t } = useTranslation();
  const [step, setStep] = useState('loading'); // loading | welcome | platform_reveal | platform_confirm | running | platform_success | platform_failed | ticket_needed | waiting_room | missed_train | on_train | arrived
  const [fade, setFade] = useState(false);
  const [token, setToken] = useState(Cookies.get('token') || null);
  const [userStatus, setUserStatus] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [passengers, setPassengers] = useState([]);
  const [journeyEvents, setJourneyEvents] = useState([]);
  const [journeyProgress, setJourneyProgress] = useState(0);
  const [journeyRemainingMs, setJourneyRemainingMs] = useState(0);
  const [hornPhase, setHornPhase] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatWarning, setChatWarning] = useState('');
  const [chatSending, setChatSending] = useState(false);
  // ── قایق عبور از دریاچه ──────────────────────────────────
  const [boatInfo, setBoatInfo] = useState(null); // { id, number, capacity, passengerCount, departedAt, full }
  const [boatRemainingMs, setBoatRemainingMs] = useState(0);
  const [boatBoarding, setBoatBoarding] = useState(false);
  const [boatChatMessages, setBoatChatMessages] = useState([]);
  const [boatChatInput, setBoatChatInput] = useState('');
  const [boatChatWarning, setBoatChatWarning] = useState('');
  const [boatChatSending, setBoatChatSending] = useState(false);
  const boatChatEndRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', invitationCode: '' });
  // ── انتخاب درگاه پرداخت (تومانی/کریپتو) ──────────────────
  // null = هنوز از سرور نیامده (تا رسیدن، فرض می‌کنیم فقط یک درگاه هست تا
  // رفتار قبلی حفظ شود). اگر بیش از یک درگاه فعال باشد (مثلاً هم زرین‌پال هم
  // OxaPay)، قبل از ساخت پرداخت یک انتخاب‌گر کوچک نشان می‌دهیم.
  const [availableGateways, setAvailableGateways] = useState(null);
  const [gatewaySelectorFor, setGatewaySelectorFor] = useState(null); // null | 'ticket' | 'package'
  const journeyTimerRef = useRef(null);
  const chatEndRef = useRef(null);
  // ── پارامترهای لینک دعوت‌نامه (کد/شماره/نام از قبل مشخص است) ─────
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // ── ویدیوی توضیحی ابتدای سفر — یک‌بار در مرحله «welcome» نمایش داده می‌شود
  // (مگر اینکه کاربر قبلاً دیده باشد یا از پنل ادمین غیرفعال شده باشد) ────────
  const [introVideo, setIntroVideo] = useState(null);
  const [showIntroVideo, setShowIntroVideo] = useState(false);

  useEffect(() => {
    fetch(`${API}/journey-video`)
      .then(r => r.json())
      .then(r => {
        if (r?.success && r.video?.enabled && r.video?.url) {
          const seen = localStorage.getItem('ph_intro_video_seen');
          if (!seen) {
            setIntroVideo(r.video);
            setShowIntroVideo(true);
          }
        }
      })
      .catch(() => {});
  }, []);

  const dismissIntroVideo = () => {
    try { localStorage.setItem('ph_intro_video_seen', '1'); } catch (_) {}
    setShowIntroVideo(false);
  };

  const countdown = useCountdown(schedule?.msUntilDeparture || 0);

  // ── احراز هویت ──────────────────────────────────────────
  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  }), [token]);

  const apiGet = useCallback(async (path) => {
    const r = await fetch(`${API}${path}`, { headers: headers() });
    return r.json();
  }, [headers]);

  const apiPost = useCallback(async (path, body = {}) => {
    const r = await fetch(`${API}${path}`, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
    return r.json();
  }, [headers]);

  // ── درگاه‌های پرداخت مجاز برای این سایت (بدون auth) ──────
  useEffect(() => {
    fetch(`${API}/payment/available-gateways`)
      .then((r) => r.json())
      .then((r) => { if (r?.success) setAvailableGateways(r.gateways || []); })
      .catch(() => setAvailableGateways([]));
  }, []);

  // ── تغییر صحنه با fade ──────────────────────────────────
  const goTo = useCallback((nextStep, delay = 0) => {
    setFade(true);
    setTimeout(() => {
      setStep(nextStep);
      setFade(false);
    }, delay + 600);
  }, []);

  // ── بارگذاری اولیه ──────────────────────────────────────
  // توجه: این افکت فقط باید یک‌بار در لود اولیه صفحه اجرا شود. چون `token` در
  // وابستگی‌هاست، هر بار که setToken بعد از ورود موفق OTP صدا زده می‌شود، این
  // افکت دوباره اجرا می‌شد و چون کاربر تازه‌ثبت‌نام‌شده هنوز trainStatus/isWizard
  // خاصی ندارد، step را به‌اشتباه به 'welcome' برمی‌گرداند — یعنی همان باگی که
  // باعث می‌شد از کاربر دوباره شماره و کد OTP خواسته شود. با این guard فقط بار
  // اول (لود صفحه) این منطق مسیریابی اجرا می‌شود.
  const initialRouteDone = useRef(false);
  useEffect(() => {
    if (initialRouteDone.current) return;
    initialRouteDone.current = true;
    (async () => {
      // زمانبندی قطار (عمومی)
      try {
        const s = await apiGet('/train/schedule');
        if (s.success) setSchedule(s.schedule);
      } catch (_) {}

      if (token) {
        // بررسی وضعیت کاربر
        try {
          const st = await apiGet('/train/my-status');
          if (st.success) {
            setUserStatus(st);
            // تعیین صحنه بر اساس وضعیت
            if (st.trainStatus === 'arrived') return setStep('arrived');
            if (st.trainStatus === 'at_boats') return setStep('at_boats');
            if (st.trainStatus === 'on_train') return setStep('on_train');
            if (st.trainStatus === 'at_station') return setStep('waiting_room');
            if (st.trainStatus === 'has_ticket') return setStep('waiting_room');
            if (st.isWizard === true) return setStep(st.hasTicket ? 'waiting_room' : 'ticket_needed');
            if (st.isWizard === false) return setStep('platform_failed');
            // isWizard هنوز null است (کاربر تست سکو را نداده) و trainStatus
            // هم 'none' است — یعنی کاربر لاگین معتبر دارد ولی هنوز به سکو
            // نرفته. باگ اصلی همین‌جا بود: قبلاً این حالت به هیچ‌کدام از
            // شرط‌های بالا نمی‌خورد و بدون return به خط پایین (setStep('welcome'))
            // می‌رسید، یعنی کاربرِ لاگین‌شده دوباره به فرم «شماره موبایل» پرت
            // می‌شد (دقیقاً چیزی که کاربر گزارش داد: از سکو رد شو → دوباره
            // صفحه‌ی وارد کردن شماره). چون این مسیر با کلیک روی لینک <a
            // href="/journey"> در داشبورد (ری‌لود کامل صفحه) هم فعال می‌شود،
            // مستقل از اینکه initialRouteDone قبلاً true بوده یا نه. پس اینجا
            // باید صریحاً کاربرِ توکن‌دار را به صحنه‌ی سکو ببریم، نه ورود.
            return setStep('platform_reveal');
          }
        } catch (_) {}
        return setStep('welcome');
      }

      // ── ورود مستقیم از لینک دعوت‌نامه ────────────────────────
      // اگر کاربر از لینک دعوت‌نامه (که کد و شماره را همراه دارد) به اینجا
      // آمده، دیگر لازم نیست نام/شماره/کد را از نو تایپ کند — همین‌جا و
      // خودکار برایش کد پیامکی درخواست می‌شود و مستقیم صفحه‌ی OTP نشان
      // داده می‌شود.
      const inviteCode = searchParams.get('code');
      const invitePhone = searchParams.get('phone');
      if (inviteCode && invitePhone) {
        setForm(p => ({
          ...p,
          invitationCode: inviteCode,
          phone: invitePhone,
          firstName: searchParams.get('firstName') || p.firstName,
          lastName: searchParams.get('lastName') || p.lastName,
        }));
        try {
          const r = await apiPost('/auth/request-otp', { phoneNumber: invitePhone, inviteCode });
          if (r.success) return setStep('otp');
        } catch (_) {}
        // اگر ارسال خودکار ناموفق بود، فرم را با اطلاعات از‌پیش‌پرشده نشان بده
        // (کاربر فقط کافیست دکمه را بزند، دیگر نیازی به تایپ دوباره نیست)
      }

      setStep('welcome');
    })();
  }, [token, apiGet, apiPost, searchParams]);

  // ── لود مسافران ─────────────────────────────────────────
  const loadPassengers = useCallback(async () => {
    try {
      const d = await apiGet('/train/station');
      if (d.success) {
        setPassengers(d.passengers || []);
        if (d.departure) {
          const ms = new Date(d.departure) - Date.now();
          setSchedule(prev => ({ ...prev, msUntilDeparture: Math.max(0, ms) }));
        }
      }
    } catch (_) {}
  }, [apiGet]);

  useEffect(() => {
    if (step === 'waiting_room') {
      loadPassengers();
      const id = setInterval(loadPassengers, 15000);
      return () => clearInterval(id);
    }
  }, [step, loadPassengers]);

  // ── بارگذاری رویدادهای سفر ──────────────────────────────
  useEffect(() => {
    if (step !== 'on_train') return;
    (async () => {
      try {
        const d = await apiGet('/train/journey-events');
        if (d.success) {
          setJourneyEvents(d.events || []);
          setJourneyRemainingMs(d.remainingMs || 0);
          // پیشرفت سفر
          const elapsed = d.totalDurationMs - (d.remainingMs || 0);
          setJourneyProgress(Math.min(100, Math.round((elapsed / d.totalDurationMs) * 100)));
        }
      } catch (_) {}
    })();
    // آپدیت هر ۳ دقیقه
    const id = setInterval(async () => {
      try {
        const d = await apiGet('/train/journey-events');
        if (d.success) {
          setJourneyEvents(d.events || []);
          setJourneyRemainingMs(d.remainingMs || 0);
          const elapsed = d.totalDurationMs - (d.remainingMs || 0);
          const pct = Math.min(100, Math.round((elapsed / d.totalDurationMs) * 100));
          setJourneyProgress(pct);
          if (pct >= 100) {
            clearInterval(id);
            goTo('arrived');
          }
        }
      } catch (_) {}
    }, 3 * 60 * 1000);
    return () => clearInterval(id);
  }, [step, apiGet, goTo]);

  useEffect(() => {
    if (step !== 'on_train') {
      setHornPhase(0);
      return;
    }
    // بوق اول
    setHornPhase(1);
    playTrainHorn();
    const ids = [
      // بوق دوم
      setTimeout(() => { setHornPhase(2); playTrainHorn(); }, 1800),
      // بوق سوم
      setTimeout(() => { setHornPhase(3); playTrainHorn(); }, 3600),
      // پنهان شدن نمایش بوق
      setTimeout(() => setHornPhase(4), 5400),
    ];
    return () => ids.forEach(clearTimeout);
  }, [step]);

  // اسکرول خودکار چت به پایین
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // ── چت گروهی قطار ───────────────────────────────────────
  const loadChat = useCallback(async () => {
    try {
      const d = await apiGet('/train/chat');
      if (d.success) {
        setChatMessages(d.messages || []);
      } else if (d.code === 'BANNED') {
        Cookies.remove('token');
        setToken(null);
        setStep('banned');
      }
    } catch (_) {}
  }, [apiGet]);

  useEffect(() => {
    if (step !== 'on_train') return;
    loadChat();
    const id = setInterval(loadChat, 5000);
    return () => clearInterval(id);
  }, [step, loadChat]);

  // ── وضعیت قایق (پس از پیاده شدن از قطار) ──────────────────
  const loadBoatStatus = useCallback(async () => {
    try {
      const d = await apiGet('/boat/my-status');
      if (d.success) {
        setBoatInfo(d.boat || null);
        setBoatRemainingMs(d.remainingMs || 0);
        if (d.trainStatus === 'arrived') {
          goTo('arrived');
        }
      }
    } catch (_) {}
  }, [apiGet, goTo]);

  // ── چت گروهی قایق ─────────────────────────────────────────
  const loadBoatChat = useCallback(async () => {
    try {
      const d = await apiGet('/boat/chat');
      if (d.success) {
        setBoatChatMessages(d.messages || []);
      } else if (d.code === 'BANNED') {
        Cookies.remove('token');
        setToken(null);
        setStep('banned');
      }
    } catch (_) {}
  }, [apiGet]);

  useEffect(() => {
    if (step !== 'at_boats') return;
    loadBoatStatus();
    const id = setInterval(loadBoatStatus, 5000);
    return () => clearInterval(id);
  }, [step, loadBoatStatus]);

  // اسکرول خودکار چت قایق به پایین
  useEffect(() => {
    if (boatChatEndRef.current) {
      boatChatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [boatChatMessages]);

  // چت قایق فقط پس از سوار شدن فعال می‌شود
  useEffect(() => {
    if (step !== 'at_boats' || !boatInfo) return;
    loadBoatChat();
    const id = setInterval(loadBoatChat, 5000);
    return () => clearInterval(id);
  }, [step, boatInfo, loadBoatChat]);

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatSending) return;
    setChatSending(true);
    setChatWarning('');
    try {
      const r = await apiPost('/train/chat', { content: text });
      if (r.success) {
        setChatInput('');
        loadChat();
      } else if (r.code === 'BANNED') {
        Cookies.remove('token');
        setToken(null);
        setStep('banned');
      } else if (r.code === 'WARNING') {
        setChatInput('');
        setChatWarning(r.message || 'این پیام با قوانین گفتگو هم‌خوان نبود و ارسال نشد.');
      } else {
        setChatWarning(r.message || 'ارسال پیام ناموفق بود.');
      }
    } catch (_) {
      setChatWarning('خطا در ارسال پیام.');
    } finally {
      setChatSending(false);
    }
  };

  // ── مرحله‌ی اول: فقط شماره تماس ─────────────────────────
  // اگر شماره از قبل توی دیتابیس باشه (کاربر قدیمی/دعوت‌شده)، اصلاً نام و
  // کد دعوت پرسیده نمی‌شه — مستقیم کد پیامکی ارسال می‌شه.
  const [phoneStage, setPhoneStage] = useState('phone'); // 'phone' | 'details'
  const handleCheckPhone = async () => {
    setError('');
    if (!form.phone.trim()) {
      return setError('لطفاً شماره موبایل را وارد کنید.');
    }
    setLoading(true);
    try {
      const check = await apiPost('/auth/check-phone', { phoneNumber: form.phone.trim() });
      if (!check.success) {
        setError(check.message || 'خطا در بررسی شماره'); setLoading(false); return;
      }
      if (check.exists) {
        // کاربر قدیمی — مستقیم کد پیامکی ارسال شود، بدون نام/کد دعوت
        const r = await apiPost('/auth/request-otp', { phoneNumber: form.phone.trim() });
        setLoading(false);
        if (!r.success) { setError(r.message || 'خطا در ارسال کد'); return; }
        return setStep('otp');
      }
      // کاربر جدید — حالا نام/نام‌خانوادگی/کد دعوت لازم است
      setPhoneStage('details');
    } catch (_) { setError('خطا در اتصال به سرور'); }
    setLoading(false);
  };

  // ── ثبت‌نام (فقط کاربر جدید) ──────────────────────────────
  const handleRegister = async () => {
    setError('');
    if (!form.firstName || !form.lastName || !form.phone) {
      return setError('لطفاً نام، نام خانوادگی و شماره موبایل را وارد کنید.');
    }
    // نکته: نام/نام‌خانوادگی/کد دعوت فقط برای کاربر جدید لازم است. کاربری که
    // قبلاً با همین شماره ثبت‌نام کرده، سرور بدون نیاز به کد دعوت اجازه ورود
    // می‌دهد — پس اینجا دیگر به‌صورت کورکورانه به صفحه «بدون دعوت‌نامه» هدایت
    // نمی‌کنیم و تصمیم را به پاسخ سرور می‌سپاریم.
    setLoading(true);
    try {
      const r = await apiPost('/auth/request-otp', { phoneNumber: form.phone, inviteCode: form.invitationCode.trim() });
      if (!r.success) {
        if (r.code === 'REGISTRATION_CLOSED' || r.code === 'INVITE_REQUIRED') { setLoading(false); return setStep('no_invite'); }
        if (r.code === 'INVALID_INVITE') { setLoading(false); return setError(r.message || 'کد دعوت نامعتبر است.'); }
        setError(r.message || 'خطا در ارسال کد'); setLoading(false); return;
      }
      setStep('otp');
    } catch (_) { setError('خطا در اتصال به سرور'); }
    setLoading(false);
  };

  const [otp, setOtp] = useState('');
  const [ticketCode, setTicketCode] = useState('');
  const [ticketInfo, setTicketInfo] = useState(null);

  // ── لیست انتظار — ثبت‌نام همین‌جا، بدون هدایت به صفحه اصلی ──────────────
  // وقتی کاربر کد دعوت ندارد (no_invite)، همان اطلاعاتی که در فرم ورود وارد
  // کرده (نام/نام‌خانوادگی/شماره) مستقیم برای لیست انتظار استفاده می‌شود:
  // کد OTP پیامک می‌شود، همین‌جا تأیید می‌کند و ثبت می‌شود.
  const [wlStage, setWlStage] = useState('checking'); // checking | form | otp | done | already
  const [wlOtp, setWlOtp] = useState('');
  const [wlLoading, setWlLoading] = useState(false);
  const [wlError, setWlError] = useState('');
  const [wlMessage, setWlMessage] = useState('');
  const [wlJy, setWlJy] = useState('');
  const [wlJm, setWlJm] = useState('');
  const [wlJd, setWlJd] = useState('');

  // به محض ورود به صحنه‌ی no_invite، اول بررسی می‌کنیم این شماره قبلاً در
  // لیست انتظار ثبت شده یا نه (قبلاً این چک اصلاً وجود نداشت).
  useEffect(() => {
    if (step !== 'no_invite') return;
    let cancelled = false;
    (async () => {
      setWlError('');
      setWlOtp('');
      try {
        const r = await apiPost('/waitlist/check', { phone: form.phone });
        if (cancelled) return;
        if (r.success && r.onWaitlist) return setWlStage('already');
      } catch (_) {}
      if (!cancelled) setWlStage('form');
    })();
    return () => { cancelled = true; };
  }, [step, form.phone, apiPost]);

  const handleWaitlistRequestOtp = async () => {
    setWlError('');
    if (!form.firstName || form.firstName.trim().length < 2 || !form.lastName || form.lastName.trim().length < 2) {
      return setWlError('لطفاً نام و نام خانوادگی را (حداقل ۲ حرف) وارد کنید.');
    }
    setWlLoading(true);
    const birthDateValue = (wlJy && wlJm && wlJd) ? { jy: Number(wlJy), jm: Number(wlJm), jd: Number(wlJd) } : null;
    try {
      const r = await apiPost('/waitlist/request-otp', {
        phone: form.phone,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        birthDate: birthDateValue
      });
      if (r.success) {
        setWlStage('otp');
      } else if (r.code === 'ALREADY_ON_WAITLIST') {
        setWlStage('already');
      } else {
        setWlError(r.message || 'خطا در ارسال کد تأیید.');
      }
    } catch (_) { setWlError('خطا در اتصال به سرور'); }
    setWlLoading(false);
  };

  const handleWaitlistVerifyOtp = async () => {
    setWlError('');
    setWlLoading(true);
    try {
      const r = await apiPost('/waitlist/verify-otp', { phone: form.phone, otp: wlOtp });
      if (r.success) {
        setWlMessage(r.message || 'شما با موفقیت در لیست انتظار ثبت شدید!');
        setWlStage('done');
      } else if (r.code === 'ALREADY_ON_WAITLIST') {
        setWlStage('already');
      } else {
        setWlError(r.message || 'کد واردشده درست نیست.');
      }
    } catch (_) { setWlError('خطا در اتصال به سرور'); }
    setWlLoading(false);
  };
  const handleVerifyOtp = async () => {
    setError('');
    setLoading(true);
    try {
      const r = await apiPost('/auth/verify-otp', {
        phoneNumber: form.phone,
        otp,
        firstName: form.firstName,
        lastName: form.lastName,
        referralCode: form.invitationCode || undefined
      });
      if (!r.success) {
        // خطاهای مرتبط با بسته بودن ثبت‌نام یا نبود کد دعوت → صفحه لیست انتظار
        if (r.code === 'INVITE_REQUIRED' || r.code === 'REGISTRATION_CLOSED' || r.code === 'INVALID_INVITE') {
          setLoading(false);
          return setStep('no_invite');
        }
        setError(r.message || 'کد واردشده درست نیست.'); setLoading(false); return;
      }
      const newToken = r.token;
      Cookies.set('token', newToken, { expires: 30 });
      setToken(newToken);
      // بررسی وضعیت جدید
      const st = await fetch(`${API}/train/my-status`, { headers: { Authorization: `Bearer ${newToken}`, 'Content-Type': 'application/json' } }).then(x => x.json());
      if (st.success) setUserStatus(st);
      // به‌جای پرتاب مستقیم کاربر تازه‌واردشده به صحنه‌ی روایی سکو، ابتدا
      // داشبورد خودش را ببیند — راهنمای عبور از سکو به‌صورت بنر داخل
      // داشبورد نمایش داده می‌شود (UserDashboard.jsx) تا کاربر هر وقت
      // خودش خواست بتواند مسیر را ادامه دهد.
      navigate('/dashboard');
    } catch (_) { setError('خطا در اتصال'); }
    setLoading(false);
  };

  // ── آزمون سکو ───────────────────────────────────────────
  const [running, setRunning] = useState(false);
  // نتیجهٔ خام آخرین تلاش — برای نمایش پیام دقیق سرور (مثلاً «هفته‌ی دیگر
  // دوباره می‌توانی امتحان کنی» برای کاربرانی که خودشان در لیست انتظار
  // ثبت‌نام کرده‌اند و تلاش‌شان نامحدود است) در صفحه‌ی شکست.
  const [platformResult, setPlatformResult] = useState(null);
  const handleTryPlatform = async () => {
    setStep('running');
    setRunning(true);
    // بعد از ۳ ثانیه انیمیشن، نتیجه بگیر
    setTimeout(async () => {
      try {
        const r = await apiPost('/game/test/platform-9-3-4');
        setRunning(false);
        setPlatformResult(r);
        if (r.passed) {
          setStep('platform_success');
          // به‌روزرسانی وضعیت
          const st = await apiGet('/train/my-status');
          if (st.success) setUserStatus(st);
        } else {
          setStep('platform_failed');
        }
      } catch (_) {
        setRunning(false);
        setStep('platform_failed');
      }
    }, 3200);
  };

  // ── ورود به ایستگاه ──────────────────────────────────────
  const handleCheckin = async () => {
    setError('');
    setLoading(true);
    try {
      const r = await apiPost('/train/checkin');
      if (r.success) {
        goTo('waiting_room');
      } else {
        setError(r.message || 'خطا در ورود به ایستگاه');
      }
    } catch (_) { setError('خطا در اتصال'); }
    setLoading(false);
  };

  // ── بررسی کد بلیت (تأیید داشتن بلیت و زمان حرکت) ──────────
  const handleVerifyTicket = async () => {
    setError('');
    const code = ticketCode.trim();
    if (!code) { setError('لطفاً کد بلیت قطار را وارد کنید.'); return; }
    setLoading(true);
    try {
      const r = await apiPost('/train/verify-ticket', { ticketCode: code });
      if (r.success) {
        setTicketInfo({
          ticketCode: r.ticketCode,
          departure: r.departure,
          departureTime: r.departureTime || '11:00',
          platform: r.platform || '9¾'
        });
        // به‌روزرسانی وضعیت و زمان حرکت
        try {
          const st = await apiGet('/train/my-status');
          if (st.success) setUserStatus(st);
        } catch (_) {}
        if (r.departure) {
          const ms = new Date(r.departure) - Date.now();
          setSchedule(prev => ({ ...prev, msUntilDeparture: Math.max(0, ms) }));
        }
        goTo('waiting_room');
      } else {
        setError(r.message || 'کد بلیت نامعتبر است.');
      }
    } catch (_) { setError('خطا در اتصال به سرور'); }
    setLoading(false);
  };

  // ── خرید بلیت / بسته کامل — ساخت واقعی درخواست پرداخت ─────
  const PURCHASE_PATHS = {
    ticket: '/payment/request-train-ticket',
    package: '/payment/request-full-package'
  };

  const submitPurchase = async (kind, gateway) => {
    setError('');
    setGatewaySelectorFor(null);
    setLoading(true);
    try {
      const r = await apiPost(PURCHASE_PATHS[kind], gateway ? { gateway } : {});
      if (r.paymentUrl) {
        window.location.href = r.paymentUrl;
      } else {
        setError(r.message || r.error || 'خطا در ایجاد پرداخت');
      }
    } catch (_) { setError('خطا در اتصال'); }
    setLoading(false);
  };

  // اگر بیش از یک درگاه فعال باشد، اول انتخاب‌گر را نشان بده؛ وگرنه (یا تا
  // زمانی که لیست از سرور نرسیده) دقیقاً مثل قبل مستقیم پرداخت بساز.
  const handleBuyTicket = () => {
    if (availableGateways && availableGateways.length > 1) { setGatewaySelectorFor('ticket'); return; }
    submitPurchase('ticket', availableGateways?.[0]);
  };

  const handleBuyPackage = () => {
    if (availableGateways && availableGateways.length > 1) { setGatewaySelectorFor('package'); return; }
    submitPurchase('package', availableGateways?.[0]);
  };

  const GATEWAY_CHOICE_LABELS = {
    zarinpal: { icon: '💳', title: 'کارت بانکی (تومان)', desc: 'پرداخت مستقیم با زرین‌پال' },
    zibal: { icon: '💳', title: 'کارت بانکی (تومان)', desc: 'پرداخت مستقیم با زیبال' },
    oxapay: { icon: '🪙', title: 'ارز دیجیتال (کریپتو)', desc: 'پرداخت با تتر/بیت‌کوین و ... از طریق OxaPay' }
  };

  // انتخاب‌گر درگاه — به‌عنوان overlay روی صحنه فعلی نشان داده می‌شود؛ در هر دو
  // بلوکی که خرید را شروع می‌کنند (ticket_needed و platform_failed) رندر می‌شود.
  const renderGatewaySelector = () => {
    if (!gatewaySelectorFor) return null;
    const kind = gatewaySelectorFor;
    return (
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 500,
          background: 'rgba(10,6,20,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
        }}
        onClick={() => setGatewaySelectorFor(null)}
      >
        <div
          className="welcome-card"
          style={{ maxWidth: 420, width: '100%' }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3 style={{ marginBottom: '0.75rem', textAlign: 'center' }}>روش پرداخت را انتخاب کن</h3>
          {(availableGateways || []).map((gw) => (
            <div
              key={gw}
              className="ticket-option"
              style={{ marginBottom: '0.75rem' }}
              onClick={() => submitPurchase(kind, gw)}
            >
              <div className="ticket-option-title">{GATEWAY_CHOICE_LABELS[gw]?.icon} {GATEWAY_CHOICE_LABELS[gw]?.title || gw}</div>
              <div className="ticket-option-desc">{GATEWAY_CHOICE_LABELS[gw]?.desc || ''}</div>
            </div>
          ))}
          <button className="btn" style={{ width: '100%', marginTop: '0.25rem' }} onClick={() => setGatewaySelectorFor(null)}>
            انصراف
          </button>
        </div>
      </div>
    );
  };

  // ── رسیدن به ایستگاه روستا → مرحله قایق‌های عبور از دریاچه ─
  const handleArriveHogwarts = async () => {
    setLoading(true);
    try {
      const r = await apiPost('/train/arrive');
      if (r.success) goTo('at_boats');
      else setError(r.message);
    } catch (_) { setError('خطا در اتصال'); }
    setLoading(false);
  };

  // ── سوار شدن به قایق (تخصیص خودکار) ──────────────────────
  const handleBoardBoat = async () => {
    setBoatBoarding(true);
    setError('');
    try {
      const r = await apiPost('/boat/board', {});
      if (r.success) {
        // وضعیت دقیق (شامل remainingMs محاسبه‌شده) را از سرور می‌گیریم تا
        // زمان‌شمار از همان لحظه درست شروع شود، نه از صفر
        await loadBoatStatus();
      } else {
        setError(r.message || 'خطا در سوار شدن به قایق');
      }
    } catch (_) { setError('خطا در اتصال'); }
    setBoatBoarding(false);
  };

  // ── ورود نهایی به قلعه هاگوارتز (پس از عبور از دریاچه) ────
  const handleArriveAtCastle = async () => {
    setLoading(true);
    try {
      const r = await apiPost('/boat/arrive');
      if (r.success) goTo('arrived');
      else setError(r.message);
    } catch (_) { setError('خطا در اتصال'); }
    setLoading(false);
  };

  const sendBoatChat = async () => {
    const text = boatChatInput.trim();
    if (!text || boatChatSending) return;
    setBoatChatSending(true);
    setBoatChatWarning('');
    try {
      const r = await apiPost('/boat/chat', { content: text });
      if (r.success) {
        setBoatChatInput('');
        loadBoatChat();
      } else if (r.code === 'BANNED') {
        Cookies.remove('token');
        setToken(null);
        setStep('banned');
      } else if (r.code === 'WARNING') {
        setBoatChatInput('');
        setBoatChatWarning(r.message || 'این پیام با قوانین گفتگو هم‌خوان نبود و ارسال نشد.');
      } else {
        setBoatChatWarning(r.message || 'ارسال پیام ناموفق بود.');
      }
    } catch (_) {
      setBoatChatWarning('خطا در ارسال پیام.');
    } finally {
      setBoatChatSending(false);
    }
  };

  // ════════════════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════════════════

  const sceneClass = `scene${fade ? ' fade-out' : ''}`;
  const journeyLeft = useCountdown(journeyRemainingMs);
  const boatLeft = useCountdown(boatRemainingMs);

  // ── Loading ──────────────────────────────────────────────
  if (step === 'loading') {
    return (
      <div className="journey-root">
        <Stars />
        <div className={sceneClass}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  // ── خوش‌آمد ─────────────────────────────────────────────
  if (step === 'welcome') {
    if (showIntroVideo && introVideo) {
      return (
        <div className="journey-root">
          <Stars />
          <div className={sceneClass}>
            <div
              style={{
                width: '100%',
                maxWidth: 560,
                background: 'rgba(20,14,32,0.9)',
                border: '1px solid rgba(212,160,23,0.35)',
                borderRadius: 16,
                padding: '20px',
                textAlign: 'center'
              }}
            >
              {introVideo.title && (
                <h2 className="welcome-title cinzel" style={{ fontSize: '1.3rem', marginBottom: 8 }}>
                  {introVideo.title}
                </h2>
              )}
              {introVideo.description && (
                <p className="welcome-subtitle" style={{ marginBottom: 16 }}>{introVideo.description}</p>
              )}
              <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', borderRadius: 10, overflow: 'hidden' }}>
                <iframe
                  src={introVideo.url}
                  title="Intro video"
                  allowFullScreen
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                />
              </div>
              <button className="btn btn-gold" style={{ marginTop: '1.25rem' }} onClick={dismissIntroVideo}>
                {t('journey.continue')}
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="journey-root">
        <Stars />
        <Owls />
        <div className={sceneClass}>
          <div className="welcome-seal">🦅</div>
          <h1 className="welcome-title cinzel">{t('journey.welcomeTitle')}</h1>
          <p className="welcome-subtitle">{t('journey.welcomeSubtitle')}</p>
          <div className="welcome-card">
            <label>{t('journey.labelPhone')}</label>
            <input type="tel" placeholder={t('journey.placeholderPhone')} value={form.phone}
              disabled={phoneStage === 'details'}
              onChange={e => setForm(p => ({...p, phone: sanitizePhoneInput(e.target.value)}))} />

            {phoneStage === 'phone' && (
              <>
                {error && <div className="error-banner">{error}</div>}
                <button className="btn btn-gold" style={{marginTop:'1.25rem'}} onClick={handleCheckPhone} disabled={loading}>
                  {loading ? t('journey.loadingCheck') : t('journey.continue')}
                </button>
              </>
            )}

            {phoneStage === 'details' && (
              <>
                <label>{t('journey.labelFirstName')}</label>
                <input placeholder={t('journey.placeholderFirstName')} value={form.firstName}
                  onChange={e => setForm(p => ({...p, firstName: e.target.value}))} />
                <label>{t('journey.labelLastName')}</label>
                <input placeholder={t('journey.placeholderLastName')} value={form.lastName}
                  onChange={e => setForm(p => ({...p, lastName: e.target.value}))} />
                <label>{t('journey.labelInviteCode')}</label>
                <input placeholder={t('journey.placeholderInviteCode')} value={form.invitationCode}
                  onChange={e => setForm(p => ({...p, invitationCode: e.target.value}))} />
                {error && <div className="error-banner">{error}</div>}
                <button className="btn btn-gold" style={{marginTop:'1.25rem'}} onClick={handleRegister} disabled={loading}>
                  {loading ? t('journey.loadingSend') : t('journey.enterWizardWorld')}
                </button>
                <button className="btn btn-ghost" onClick={() => { setError(''); setPhoneStage('phone'); }}> {t('journey.notMyNumber')} </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── بدون کد دعوت — ثبت‌نام در لیست انتظار همین‌جا ────────
  // دیگر به صفحه اصلی (/#waitlist) هدایت نمی‌کنیم: اطلاعاتی که کاربر همین
  // الان در فرم ورود وارد کرده استفاده می‌شود، OTP پیامک می‌شود و همین‌جا
  // تأیید و ثبت می‌شود. اگر قبلاً ثبت شده باشد هم همین‌جا اطلاع داده می‌شود.
  if (step === 'no_invite') {
    return (
      <div className="journey-root">
        <Stars />
        <div className={sceneClass}>
          <div className="welcome-seal" style={{fontSize:'3.5rem'}}>✉️</div>
          <h2 className="welcome-title cinzel" style={{fontSize:'1.5rem'}}>ثبت‌نام عمومی هنوز آغاز نشده است</h2>

          {wlStage === 'checking' && <div className="spinner" />}

          {wlStage === 'already' && (
            <>
              <div className="narrative-text">
                <p>شماره <strong>{form.phone}</strong> از قبل در <strong>لیست انتظار</strong> ثبت شده است. 🦉</p>
                <br />
                <p>به محض باز شدن ثبت‌نام یا رسیدن نوبت شما، جغدها دعوت‌نامه را برایتان می‌آورند!</p>
              </div>
              <br />
              <button className="btn btn-ghost" style={{maxWidth:'300px'}}
                onClick={() => { setError(''); setStep('welcome'); }}>
                {t('journey.dashboardLink')}
              </button>
            </>
          )}

          {wlStage === 'form' && (
            <>
              <div className="narrative-text">
                <p>{t('journey.waitlistOnlyInvites')}</p>
                <br />
                <p>{t('journey.waitlistRegisterPrompt')}</p>
              </div>
              <div className="welcome-card">
                <label>نام *</label>
                <input placeholder="هری" value={form.firstName}
                  onChange={e => setForm(p => ({...p, firstName: e.target.value}))} />
                <label>نام خانوادگی *</label>
                <input placeholder={t('journey.placeholderLastName')} value={form.lastName}
                  onChange={e => setForm(p => ({...p, lastName: e.target.value}))} />
                
                <label>{t('journey.labelBirthDate')}</label>
                <div style={{ display: 'flex', gap: '8px', width: '100%', marginBottom: '1.25rem' }}>
                  <select
                    value={wlJd}
                    onChange={(e) => setWlJd(e.target.value)}
                    style={{ flex: 1, padding: '10px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: '#fff' }}
                  >
                    <option value="" style={{ background: '#1b1430' }}>{t('journey.selectDay')}</option>
                    {Array.from({ length: jalaliDaysInMonth(wlJm) }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d} style={{ background: '#1b1430' }}>{d}</option>
                    ))}
                  </select>
                  <select
                    value={wlJm}
                    onChange={(e) => { setWlJm(e.target.value); setWlJd(''); }}
                    style={{ flex: 1, padding: '10px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: '#fff' }}
                  >
                    <option value="" style={{ background: '#1b1430' }}>{t('journey.selectMonth')}</option>
                    {JALALI_MONTHS.map((name, i) => (
                      <option key={name} value={i + 1} style={{ background: '#1b1430' }}>{name}</option>
                    ))}
                  </select>
                  <select
                    value={wlJy}
                    onChange={(e) => setWlJy(e.target.value)}
                    style={{ flex: 1, padding: '10px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: '#fff' }}
                  >
                    <option value="" style={{ background: '#1b1430' }}>{t('journey.selectYear')}</option>
                    {JALALI_YEARS.map((y) => (
                      <option key={y} value={y} style={{ background: '#1b1430' }}>{y}</option>
                    ))}
                  </select>
                </div>

                <label>شماره موبایل</label>
                <input type="tel" value={form.phone} disabled />
                {wlError && <div className="error-banner">{wlError}</div>}
                <button className="btn btn-gold" style={{marginTop:'1.25rem'}} onClick={handleWaitlistRequestOtp} disabled={wlLoading}>
                  {wlLoading ? t('journey.waitlistSending') : t('journey.waitlistRegister')}
                </button>
                <button className="btn btn-ghost" onClick={() => { setError(''); setStep('welcome'); }}>{t('journey.back')}</button>
              </div>
            </>
          )}

          {wlStage === 'otp' && (
            <>
              <p className="welcome-subtitle">{t('journey.enterOtpPrompt', { phone: form.phone })}</p>
              <div className="welcome-card">
                <label>{t('journey.labelOtp')}</label>
                <input type="text" inputMode="numeric" placeholder="123456" value={wlOtp}
                  onChange={e => setWlOtp(toEnglishDigits(e.target.value).replace(/\D/g, '').slice(0, 6))}
                  style={{textAlign:'center',fontSize:'1.5rem',letterSpacing:'0.3em'}} />
                {wlError && <div className="error-banner">{wlError}</div>}
                <button className="btn btn-gold" style={{marginTop:'1rem'}} onClick={handleWaitlistVerifyOtp} disabled={wlLoading}>
                  {wlLoading ? t('journey.waitlistVerifying') : t('journey.waitlistConfirm')}
                </button>
                <button className="btn btn-ghost" onClick={() => { setWlError(''); setWlOtp(''); setWlStage('form'); }}>{t('journey.back')}</button>
              </div>
            </>
          )}

          {wlStage === 'done' && (
            <>
              <div className="narrative-text">
                <p>{wlMessage}</p>
                <br />
                <p>درخواست شما توسط جغدها به وزارت سحر و جادو ارسال شد. منتظر دعوت‌نامه باشید! 🦉</p>
              </div>
              <br />
              <button className="btn btn-ghost" style={{maxWidth:'300px'}}
                onClick={() => { setError(''); setStep('welcome'); }}>
                بازگشت
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── OTP ─────────────────────────────────────────────────
  if (step === 'otp') {
    return (
      <div className="journey-root">
        <Stars />
        <div className={sceneClass}>
          <div className="welcome-seal" style={{fontSize:'3rem'}}>📱</div>
          <h2 className="welcome-title" style={{fontSize:'1.4rem'}}>{t('journey.otpTitle')}</h2>
          <p className="welcome-subtitle">{t('journey.enterOtpInstruction', { phone: form.phone })}</p>
          <div className="welcome-card">
            <label>کد ۶ رقمی</label>
            <input type="number" placeholder="123456" value={otp}
              onChange={e => setOtp(e.target.value)} style={{textAlign:'center',fontSize:'1.5rem',letterSpacing:'0.3em'}} />
            {error && <div className="error-banner">{error}</div>}
            <button className="btn btn-gold" style={{marginTop:'1rem'}} onClick={handleVerifyOtp} disabled={loading}>
              {loading ? t('journey.loadingVerify') : t('journey.verify')}
            </button>
            <button className="btn btn-ghost" onClick={() => { setStep('welcome'); setError(''); }}>{t('journey.back')}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── سکوی ۹ و ۳/۴ — آشکار شدن ───────────────────────────
  if (step === 'platform_reveal') {
    return (
      <div className="journey-root" style={{background:'#050508'}}>
        <Stars />
        <DashboardBackLink />
        <div className="mist-overlay" />
        <div className={sceneClass}>
          <div className="platform-number">{t('journey.platformNumber')}</div>
          <div className="platform-wall-visual" />
          <div className="narrative-text">
            <p>{t('journey.platformInfo')}</p>
            <br />
            <p>{t('journey.platformRealMagic')}</p>
            <br />
            <p>{t('journey.platformQuestion')}</p>
          </div>
          <br />
          {userStatus?.fullPackagePurchased ? (
            <>
              <div className="info-box" style={{textAlign:'center'}}>
                <strong>{t('journey.fullPackageActive')}</strong><br />{t('journey.fullPackageInfo')}
              </div>
              <button className="btn btn-gold" style={{maxWidth:'300px'}} onClick={handleTryPlatform}>
                {t('journey.enterPlatform')}
              </button>
            </>
          ) : (
            <button className="btn btn-gold" style={{maxWidth:'340px'}} onClick={() => goTo('platform_confirm')}>
              {t('journey.moveToWall')}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── تأیید دوم ────────────────────────────────────────────
  if (step === 'platform_confirm') {
    return (
      <div className="journey-root" style={{background:'#050508'}}>
        <Stars />
        <DashboardBackLink />
        <div className="mist-overlay" />
        <div className={sceneClass}>
          <div className="welcome-seal" style={{fontSize:'4rem'}}>{t('journey.warningIcon')}</div>
          <div className="narrative-text">
            <p>{t('journey.notWizardWarning')}</p>
            <br />
            <p>{t('journey.wallImpact')}</p>
            <br />
            <p>{t('journey.confirmContinue')}</p>
          </div>
          <br />
          <button className="btn btn-danger" style={{maxWidth:'320px'}} onClick={handleTryPlatform}>
            {t('journey.acceptRisk')}
          </button>
          <button className="btn btn-ghost" style={{maxWidth:'320px',marginTop:'0.75rem'}}
            onClick={() => goTo('platform_reveal')}>
            {t('journey.stillUnsure')}
          </button>
        </div>
      </div>
    );
  }

  // ── دویدن به سمت دیوار (نمای ۳ بعدی فوق‌العاده واقعی) ───────────
  if (step === 'running') {
    return (
      <div className="journey-root">
        <div className="running-scene-3d">
          <div className="vignette" />
          
          <div className="magic-sparks">
            {[...Array(60)].map((_, i) => (
              <div key={i} className="magic-spark" style={{
                '--angle': `${Math.random() * 360}deg`,
                '--delay': `${Math.random() * 1.5}s`,
                '--dur': `${0.2 + Math.random() * 0.3}s`
              }} />
            ))}
          </div>
          
          <div className="camera-shake">
            <div className="brick-wall-3d">
              <div className="brick-texture" />
              <div className="magical-portal-glow" />
            </div>
          </div>
          
          <div className="running-progress-3d">
            <p className="cinzel">{t('journey.runningThroughWall')}</p>
            <div className="running-progress-bar">
              <div className="running-progress-fill" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── موفقیت در عبور ───────────────────────────────────────
  if (step === 'platform_success') {
    const hasTicket = userStatus?.hasTicket;
    return (
      <div className="journey-root">
        <div className="platform-success-bg">
          <div className="ambient-steam" />
          <div className="ambient-steam" style={{animationDelay:'-5s', opacity: 0.6}} />
          <div className="train-silhouette">🚂</div>
        </div>
        <div className="pass-flash" />
        <DashboardBackLink />
        <div className={sceneClass} style={{ zIndex: 10 }}>
          <div className="success-glass-card">
            <div className="result-icon">✨</div>
            <h2 className="result-title success cinzel">{t('journey.successTitle')}</h2>
            <div className="narrative-text">
              <p>{t('journey.wallVanish')}</p>
              <br />
              <p>{t('journey.steamAllAround')}</p>
              <br />
              <p>{t('journey.owlsFly')}</p>
              <br />
              <p>{t('journey.successMessage')}</p>
              <br />
              {hasTicket ? (
                <p><strong style={{color:'var(--gold)'}}>بلیت قطار شما: {userStatus?.ticketCode}</strong></p>
              ) : (
                <p>اما <em>سفر تو تازه آغاز شده است...</em></p>
              )}
            </div>
            <br />
            {hasTicket ? (
              <button className="btn btn-gold" style={{maxWidth:'300px', margin: '0 auto'}} onClick={() => goTo('waiting_room')}>
                🚂 ورود به سکوی قطار
              </button>
            ) : (
              <button className="btn btn-gold" style={{maxWidth:'300px', margin: '0 auto'}} onClick={() => goTo('ticket_needed')}>
                برویم سراغ بلیت →
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── شکست در عبور ─────────────────────────────────────────
  if (step === 'platform_failed') {
    return (
      <div className="journey-root">
        <div className="platform-failed-bg">
           <div className="brick-texture" style={{filter: 'brightness(0.2) blur(1px)'}} />
           <div className="dizzy-stars">
             <span>💫</span><span>💫</span><span>💫</span>
           </div>
        </div>
        <div className="crash-flash" />
        <DashboardBackLink />
        <div className={sceneClass} style={{ zIndex: 10 }}>
          <div className="failure-glass-card">
            <div className="result-icon">😵</div>
            <h2 className="result-title failure">متأسفیم...</h2>
            <div className="narrative-text">
              <p>به نظر می‌رسد جادوی کافی برای<br />عبور از سکو در وجودت بیدار نشده است.</p>
              <br />
              <p>شاید دفعه بعد...</p>
              <br />
              <p>یا شاید هم باید یک جغد، نامه‌ای برایت بیاورد...</p>
              {platformResult?.message && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: 'rgba(59, 130, 246, 0.08)',
                  borderRadius: '8px',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  textAlign: 'center'
                }}>
                  <p style={{ fontSize: '0.9rem', color: '#eee', direction: 'rtl', margin: 0, lineHeight: '1.6' }}>
                    {platformResult.message}
                  </p>
                </div>
              )}
              <div style={{
                marginTop: '1.5rem',
                padding: '1.2rem',
                background: 'rgba(211, 166, 37, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(211, 166, 37, 0.4)',
                textAlign: 'center'
              }}>
                <p style={{ fontSize: '0.85rem', color: '#eee', marginBottom: '0.8rem', direction: 'rtl', lineHeight: '1.5' }}>
                  🧙‍♂️ نگران نباش! برای قوی‌تر کردن نیروی جادویی خود، به گروه تمرینی سکوی ۹ و ۳/۴ در تلگرام ملحق شو تا با تمرین بیشتر بتوانی در تلاش‌های بعدی عبور کنی:
                </p>
                <a
                  href="https://t.me/+arD1IG7cRXE5YTk0"
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-gold"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '0.6rem 1.2rem',
                    fontSize: '0.85rem',
                    textDecoration: 'none'
                  }}
                >
                  📨 ورود به گروه تمرینی سکوی ۹ و ۳/۴
                </a>
              </div>
            </div>
            <br />
            <div style={{width:'100%',maxWidth:'440px', margin: '0 auto'}}>
              <p style={{color:'var(--muted)',textAlign:'center',marginBottom:'1rem',fontSize:'0.9rem'}}>
                می‌توانید یکی از گزینه‌های زیر را انتخاب کنید:
              </p>
              <div className="ticket-option" onClick={handleBuyPackage}>
                <div className="ticket-option-badge">{t('journey.specialOfferBadge')}</div>
                <div className="ticket-option-title">🎟️ {t('journey.fullPackageTitle')}</div>
                <div className="ticket-option-desc">
                    {t('journey.fullPackageDescription')}
                </div>
                <div className="ticket-option-price">۵۰۰,۰۰۰ تومان</div>
            </div>
          </div>
          {error && <div className="error-banner">{error}</div>}
        </div>
        {renderGatewaySelector()}
        </div>
      </div>
    );
  }

  // ── نیاز به بلیت ─────────────────────────────────────────
  if (step === 'ticket_needed') {
    return (
      <div className="journey-root">
        <Stars />
        <DashboardBackLink />
        <div className={sceneClass}>
          <div className="welcome-seal" style={{fontSize:'4rem'}}>🎫</div>
          <h2 className="welcome-title">بلیت قطار هاگوارتز</h2>
          <div className="narrative-text" style={{marginBottom:'1.5rem'}}>
            <p>تو موفق شدی وارد سکوی جادوگران شوی...</p>
            <br />
            <p>اما قطار بدون بلیت هیچ مسافری را سوار نمی‌کند.</p>
            <br />
            <p>اگر کد بلیت داری، همین‌جا وارد کن تا زمان حرکتت مشخص شود.</p>
          </div>

          {/* ورود کد بلیت برای تأیید */}
          <div className="welcome-card" style={{marginBottom:'1.25rem'}}>
            <label>کد بلیت قطار</label>
            <input
              placeholder="مثلاً HWX-XXXX"
              value={ticketCode}
              onChange={e => setTicketCode(e.target.value)}
              style={{textAlign:'center',letterSpacing:'0.15em',textTransform:'uppercase'}}
            />
            <button className="btn btn-gold" style={{marginTop:'1rem'}} onClick={handleVerifyTicket} disabled={loading}>
              {loading ? 'در حال بررسی...' : '🎫 بررسی و تأیید بلیت'}
            </button>
          </div>

          <p style={{color:'var(--muted)',fontSize:'0.8rem',marginBottom:'1rem'}}>یا بلیت تهیه کن</p>

          <div className="ticket-option" onClick={handleBuyTicket}>
            <div className="ticket-option-title">🎟️ بلیت قطار هاگوارتز اکسپرس</div>
            <div className="ticket-option-desc">صدور بلیت رسمی برای سفر به هاگوارتز</div>
            <div className="ticket-option-price">۵۰۰,۰۰۰ تومان</div>
          </div>

          <p style={{color:'var(--muted)',fontSize:'0.8rem',marginTop:'0.5rem',marginBottom:'1rem'}}>یا</p>

          <div className="ticket-option" onClick={handleBuyPackage} style={{borderColor:'rgba(201,168,76,0.5)'}}>
            <div className="ticket-option-badge">ارزش بیشتر</div>
            <div className="ticket-option-title">🌟 بسته کامل ورود</div>
            <div className="ticket-option-desc">
              دعوت‌نامه رسمی + عبور تضمینی + بلیت قطار<br/>
              (برای دفعات بعدی هم استفاده می‌شود)
            </div>
            <div className="ticket-option-price">۵۰۰,۰۰۰ تومان</div>
          </div>

          {error && <div className="error-banner">{error}</div>}
        </div>
        {renderGatewaySelector()}
      </div>
    );
  }

  // ── سالن انتظار ──────────────────────────────────────────
  if (step === 'waiting_room') {
    const hasTicket = userStatus?.hasTicket;
    const alreadyCheckedIn = userStatus?.trainStatus === 'at_station';
    const inWindow = schedule?.isInBoardingWindow;
    const departed = schedule?.isDeparted;

    if (departed) {
      // قطار حرکت کرده
      return (
        <div className="journey-root">
          <Stars />
          <DashboardBackLink />
          <div className={sceneClass}>
            <div className="result-icon">🌫️</div>
            <h2 className="result-title failure cinzel">قطار حرکت کرد...</h2>
            <div className="narrative-text">
              <p>تو چند لحظه دیر رسیدی.</p>
              <br />
              <p>فقط دود قطار را دیدی که از ایستگاه دور شد...</p>
              <br />
              <p>حالا باید تا جمعه آینده منتظر بمانی.</p>
            </div>
            <div className="info-box" style={{textAlign:'center',marginTop:'1.5rem'}}>
              <strong>قطار بعدی:</strong><br />
              جمعه آینده ساعت ۱۱:۰۰ صبح
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="journey-root">
        <Stars />
        <Owls />
        <DashboardBackLink />
        <div className={sceneClass}>
          <div className="train-station-set" aria-hidden>
            <div className="station-clock">۱۱:۰۰</div>
            <div className="platform-sign">Platform 9¾</div>
            <div className="station-arches">
              <span />
              <span />
              <span />
            </div>
            <div className="station-lamps">
              <span />
              <span />
            </div>
            <div className="station-rail" />
          </div>

          <div className="station-header">
            <div className="train-img">🚂</div>
            <div className="steam-container" aria-hidden>
              {[...Array(6)].map((_, i) => (
                <div key={i} className="steam" style={{
                  width: 30 + i * 10, height: 30 + i * 10,
                  left: `${10 + i * 14}%`,
                  '--dur': `${2.5 + i * 0.4}s`,
                  '--delay': `${i * 0.6}s`
                }} />
              ))}
            </div>
            <h2 className="welcome-title cinzel" style={{fontSize:'1.4rem'}}>سکوی ۹¾</h2>
            <p className="welcome-subtitle">ایستگاه هاگوارتز اکسپرس</p>
          </div>

          {/* شمارش معکوس */}
          <div className="countdown-display">{countdown.formatted}</div>
          <p className="countdown-label">تا حرکت قطار</p>

          {ticketInfo && (
            <div className="info-box" style={{textAlign:'center',marginBottom:'1rem'}}>
              🎫 بلیت شما: <strong>{ticketInfo.ticketCode}</strong><br />
              ساعت حرکت: <strong>{ticketInfo.departureTime}</strong> — سکوی {ticketInfo.platform}
            </div>
          )}

          {/* وضعیت بلیت / ورود */}
          {hasTicket && !alreadyCheckedIn && (
            <button className="btn btn-gold" style={{maxWidth:'300px',marginBottom:'1rem'}}
              onClick={handleCheckin} disabled={loading || !inWindow}>
              {!inWindow
                ? `درهای ایستگاه ساعت ۱۰:۳۰ باز می‌شود`
                : loading ? 'در حال ورود...' : '✅ ورود به ایستگاه'}
            </button>
          )}
          {alreadyCheckedIn && (
            <div className="success-banner" style={{maxWidth:'300px',marginBottom:'1rem'}}>
              🟢 در ایستگاه هستید — آماده سوار شدن
            </div>
          )}
          {!hasTicket && (
            <button className="btn btn-ghost" style={{maxWidth:'300px',marginBottom:'1rem'}}
              onClick={() => setStep('ticket_needed')}>
              خرید بلیت
            </button>
          )}
          {error && <div className="error-banner">{error}</div>}

          {/* لیست مسافران */}
          <div className="passengers-panel">
            <h3>👥 مسافران این هفته ({passengers.length} نفر)</h3>
            <div className="passenger-list">
              {passengers.length === 0 && (
                <p style={{color:'var(--muted)',textAlign:'center',fontSize:'0.85rem'}}>
                  هنوز کسی وارد ایستگاه نشده است
                </p>
              )}
              {passengers.map(p => (
                <div key={p.id} className="passenger-item">
                  <div className="passenger-avatar">
                    {p.profileImage
                      ? <img src={p.profileImage} alt={p.name} />
                      : '🧙'}
                  </div>
                  <span className="passenger-name">{p.name}</span>
                  {p.house && <span style={{fontSize:'0.75rem',color:'var(--muted)'}}>{p.house}</span>}
                  <span className={`passenger-status ${p.status}`}>
                    {p.status === 'at_station' ? '🟢 در ایستگاه' : '⚪ هنوز نرسیده'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── در قطار ──────────────────────────────────────────────
  if (step === 'on_train') {
    return (
      <div className="journey-root">
        <Stars />
        <DashboardBackLink />
        <div className={sceneClass}>
          <div className="journey-scene train-departure-scene">
            <div className="train-window-view" aria-hidden>
              <div className="moon" />
              <div className="passing-hills hill-a" />
              <div className="passing-hills hill-b" />
              <div className="track-lines" />
              <div className="moving-train">
                <div className="train-engine">🚂</div>
                <div className="train-car car-one" />
                <div className="train-car car-two" />
              </div>
              {hornPhase > 0 && hornPhase < 4 && (
                <div className={`horn-call horn-${hornPhase}`}>
                  📯 بوق {'•'.repeat(hornPhase)} {hornPhase}/۳
                </div>
              )}
            </div>

            <h2 className="welcome-title cinzel" style={{fontSize:'1.4rem'}}>هاگوارتز اکسپرس حرکت کرد</h2>
            <p className="welcome-subtitle" style={{marginBottom:'1rem'}}>
              سه بوق قطار زده شد؛ کوپه‌ها از میان بخار و نور ایستگاه دور می‌شوند.
            </p>

            {(() => {
              const isEnglish = window.location.hostname.includes('potterheads.org');
              return (
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: '12px',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: '0 0 0.6rem 0', fontSize: '0.88rem', color: '#eee', direction: isEnglish ? 'ltr' : 'rtl', textAlign: isEnglish ? 'left' : 'right', lineHeight: '1.4' }}>
                    {isEnglish 
                      ? "📢 Join the official Hogwarts Express train channel to stay updated with live events along the journey:"
                      : "📢 به کانال تلگرام رسمی قطار هاگوارتز اکسپرس بپیوندید تا در طول مسیر از اخبار و رویدادهای زنده باخبر شوید:"}
                  </p>
                  <a
                    href={isEnglish ? "https://t.me/+gujTO4Kbe2o1ZTU0" : "https://t.me/+90t8G96Ru241MjA0"}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-gold"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '0.55rem 1.2rem',
                      fontSize: '0.85rem',
                      textDecoration: 'none'
                    }}
                  >
                    📨 {isEnglish ? "Join the Train Channel" : "ورود به کانال تلگرام قطار"}
                  </a>
                </div>
              );
            })()}

            <div className="arrival-countdown-card">
              <span>مانده تا رسیدن به مدرسه هاگوارتز</span>
              <strong>{journeyLeft.formatted}</strong>
            </div>

            <div className="journey-progress">
              <div className="journey-progress-fill" style={{width:`${journeyProgress}%`}} />
            </div>
            <p style={{color:'var(--muted)',textAlign:'center',fontSize:'0.85rem',marginBottom:'1.5rem'}}>
              {journeyProgress}٪ مسیر طی شده
            </p>

            {journeyEvents.map((ev, i) => (
              <div key={ev.id} className="event-card" style={{'--delay':`${i*0.2}s`}}>
                <div className="event-icon">{ev.icon}</div>
                <div className="event-body">
                  <h4>{ev.title}</h4>
                  <p>{ev.text}</p>
                </div>
              </div>
            ))}

            {journeyProgress >= 90 && (
              <button className="btn btn-gold" onClick={handleArriveHogwarts} disabled={loading} style={{marginTop:'1rem'}}>
                {loading ? '...' : '🏰 ورود به هاگوارتز'}
              </button>
            )}

            {/* چت گروهی مسافران قطار */}
            <div className="train-chat" style={{marginTop:'2rem',textAlign:'right'}}>
              <h3 className="cinzel" style={{fontSize:'1.1rem',marginBottom:'0.75rem'}}>💬 گفتگوی مسافران</h3>
              <div className="train-chat-messages" style={{maxHeight:'300px',overflowY:'auto',display:'flex',flexDirection:'column',gap:'0.5rem',padding:'0.75rem',background:'rgba(0,0,0,0.25)',borderRadius:'12px',marginBottom:'0.75rem'}}>
                {chatMessages.length === 0 && (
                  <p style={{color:'var(--muted)',textAlign:'center',fontSize:'0.85rem'}}>هنوز پیامی نیست. اولین نفر باش!</p>
                )}
                {chatMessages.map((m) => (
                  <div key={m.id} style={{alignSelf: m.isSelf ? 'flex-start' : 'flex-end', maxWidth:'85%'}}>
                    <div style={{
                      background: m.isAdmin ? 'rgba(212,175,55,0.25)' : m.isSelf ? 'rgba(80,120,200,0.25)' : 'rgba(255,255,255,0.08)',
                      border: m.isAdmin ? '1px solid rgba(212,175,55,0.6)' : '1px solid rgba(255,255,255,0.1)',
                      borderRadius:'10px', padding:'0.5rem 0.75rem'
                    }}>
                      <div style={{fontSize:'0.72rem',color: m.isAdmin ? '#d4af37' : 'var(--muted)',marginBottom:'0.2rem'}}>
                        {m.isAdmin ? `🎩 ${m.name}` : m.name}
                      </div>
                      <div style={{fontSize:'0.9rem',wordBreak:'break-word'}}>{m.content}</div>
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              {chatWarning && (
                <p style={{color:'#ff9b9b',fontSize:'0.82rem',marginBottom:'0.5rem'}}>⚠️ {chatWarning}</p>
              )}
              <div style={{display:'flex',gap:'0.5rem'}}>
                <input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendChat(); }}
                  placeholder="پیامت را بنویس..."
                  maxLength={1000}
                  style={{flex:1,padding:'0.6rem 0.75rem',borderRadius:'8px',border:'1px solid rgba(255,255,255,0.15)',background:'rgba(0,0,0,0.3)',color:'#fff'}}
                />
                <button className="btn btn-gold" onClick={sendChat} disabled={chatSending}>
                  {chatSending ? '...' : 'ارسال'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── عبور از دریاچه با قایق ────────────────────────────────
  if (step === 'at_boats') {
    return (
      <div className="journey-root">
        <Stars />
        <DashboardBackLink />
        <div className={sceneClass}>
          <div className="journey-scene train-departure-scene">
            <h2 className="welcome-title cinzel" style={{fontSize:'1.4rem'}}>ایستگاه روستای هاگزمید</h2>
            <p className="welcome-subtitle" style={{marginBottom:'1rem'}}>
              قطار در ایستگاه روستا توقف کرد. برای رسیدن به قلعه هاگوارتز باید سوار یکی از قایق‌های چهارنفره شوی و از دریاچه سیاه عبور کنی 🚣‍♂️🌊
            </p>

            {error && <p style={{color:'#ff9b9b',textAlign:'center',marginBottom:'1rem'}}>{error}</p>}

            {!boatInfo && (
              <div style={{textAlign:'center'}}>
                <button className="btn btn-gold" onClick={handleBoardBoat} disabled={boatBoarding}>
                  {boatBoarding ? '...' : '🚣 سوار قایق شو'}
                </button>
              </div>
            )}

            {boatInfo && (
              <>
                <div className="arrival-countdown-card">
                  <span>مانده تا رسیدن به ساحل قلعه — قایق شماره {boatInfo.number}</span>
                  <strong>{boatLeft.formatted}</strong>
                </div>
                <p style={{color:'var(--muted)',textAlign:'center',fontSize:'0.85rem',marginBottom:'1.5rem'}}>
                  {boatInfo.passengerCount} از {boatInfo.capacity} سرنشین سوار این قایق هستند
                </p>

                {boatRemainingMs <= 0 && (
                  <div style={{textAlign:'center'}}>
                    <button className="btn btn-gold" onClick={handleArriveAtCastle} disabled={loading} style={{marginTop:'0.5rem'}}>
                      {loading ? '...' : '🏰 ورود به قلعه هاگوارتز'}
                    </button>
                  </div>
                )}

                {/* چت گروهی سرنشینان قایق */}
                <div className="train-chat" style={{marginTop:'2rem',textAlign:'right'}}>
                  <h3 className="cinzel" style={{fontSize:'1.1rem',marginBottom:'0.75rem'}}>💬 گفتگوی سرنشینان قایق</h3>
                  <div className="train-chat-messages" style={{maxHeight:'300px',overflowY:'auto',display:'flex',flexDirection:'column',gap:'0.5rem',padding:'0.75rem',background:'rgba(0,0,0,0.25)',borderRadius:'12px',marginBottom:'0.75rem'}}>
                    {boatChatMessages.length === 0 && (
                      <p style={{color:'var(--muted)',textAlign:'center',fontSize:'0.85rem'}}>هنوز پیامی نیست. اولین نفر باش!</p>
                    )}
                    {boatChatMessages.map((m) => (
                      <div key={m.id} style={{alignSelf: m.isSelf ? 'flex-start' : 'flex-end', maxWidth:'85%'}}>
                        <div style={{
                          background: m.isAdmin ? 'rgba(212,175,55,0.25)' : m.isSelf ? 'rgba(80,120,200,0.25)' : 'rgba(255,255,255,0.08)',
                          border: m.isAdmin ? '1px solid rgba(212,175,55,0.6)' : '1px solid rgba(255,255,255,0.1)',
                          borderRadius:'10px', padding:'0.5rem 0.75rem'
                        }}>
                          <div style={{fontSize:'0.72rem',color: m.isAdmin ? '#d4af37' : 'var(--muted)',marginBottom:'0.2rem'}}>
                            {m.isAdmin ? `🎩 ${m.name}` : m.name}
                          </div>
                          <div style={{fontSize:'0.9rem',wordBreak:'break-word'}}>{m.content}</div>
                        </div>
                      </div>
                    ))}
                    <div ref={boatChatEndRef} />
                  </div>
                  {boatChatWarning && (
                    <p style={{color:'#ff9b9b',fontSize:'0.82rem',marginBottom:'0.5rem'}}>⚠️ {boatChatWarning}</p>
                  )}
                  <div style={{display:'flex',gap:'0.5rem'}}>
                    <input
                      value={boatChatInput}
                      onChange={(e) => setBoatChatInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') sendBoatChat(); }}
                      placeholder="پیامت را بنویس..."
                      maxLength={1000}
                      style={{flex:1,padding:'0.6rem 0.75rem',borderRadius:'8px',border:'1px solid rgba(255,255,255,0.15)',background:'rgba(0,0,0,0.3)',color:'#fff'}}
                    />
                    <button className="btn btn-gold" onClick={sendBoatChat} disabled={boatChatSending}>
                      {boatChatSending ? '...' : 'ارسال'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── دسترسی مسدود شده ─────────────────────────────────────
  if (step === 'banned') {
    return (
      <div className="journey-root">
        <Stars />
        <div className={sceneClass} style={{paddingTop:'3rem'}}>
          <div className="journey-scene" style={{textAlign:'center'}}>
            <div style={{fontSize:'3rem',marginBottom:'1rem'}}>⛔</div>
            <h1 className="welcome-title cinzel" style={{fontSize:'1.5rem'}}>دسترسی مسدود شد</h1>
            <p style={{color:'var(--muted)',marginTop:'1rem',lineHeight:1.8}}>
              به دلیل رفتار نامناسب در گفتگوی قطار، دسترسی شما به سایت مسدود شده است.
              اگر فکر می‌کنی اشتباهی رخ داده، به پشتیبانی پیام بده.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── رسیدن به هاگوارتز ────────────────────────────────────
  if (step === 'arrived') {
    const house = userStatus?.house;
    const houseEmojis = { Gryffindor:'🦁', Slytherin:'🐍', Ravenclaw:'🦅', Hufflepuff:'🦡' };

    return (
      <div className="journey-root">
        <Stars />
        <Owls />
        <div className={sceneClass} style={{paddingTop:'3rem'}}>
          <div className="arrival-scene">
            <div className="hogwarts-castle">🏰</div>
            <h1 className="arrival-title">به هاگوارتز خوش آمدی!</h1>
            <div className="arrival-text">
              <p>تبریک!</p>
              <p>سفر تو به پایان رسید؛ اما ماجراجویی واقعی از همین لحظه آغاز می‌شود.</p>
              <p>به مدرسه علوم و فنون جادوگری هاگوارتز خوش آمدی.</p>
            </div>
            {house && (
              <div className={`house-banner ${house}`} style={{marginBottom:'1.5rem'}}>
                {houseEmojis[house] || '⚡'} خانه {house}
              </div>
            )}
            <Link to="/dashboard" className="btn btn-gold" style={{maxWidth:'280px',textDecoration:'none',display:'block',margin:'0 auto'}}>
              ورود به داشبورد جادوگری
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // fallback
  return (
    <div className="journey-root">
      <Stars />
      <div className={sceneClass}>
        <div className="spinner" />
      </div>
    </div>
  );
}
